import { t } from "@lingui/core/macro";
import { i18n } from "@lingui/core";
import { DateTime } from "luxon";
import { Location } from "../../level/level.model";
import { defaultLocale } from "../../utils/i18n";
import { PointEnergyReportDTO } from "./point-energy.queries";

export enum DataType {
  Heating = 1,
  Electricity,
  Cooling,
  ColdWater,
  HotWater,
  TotalWater,
  HeatingNormalized,
}
export const dataTypeUnits = {
  [DataType.Heating]: "mwh",
  [DataType.Electricity]: "kwh",
  [DataType.Cooling]: "mwh",
  [DataType.HotWater]: "m3",
  [DataType.ColdWater]: "m3",
  [DataType.TotalWater]: "m3",
};

export type EnergyResolution = "month" | "day" | "hour";

export const energyResolutionMap: Record<DateResolution, EnergyResolution> = {
  year: "month",
  month: "day",
  day: "hour",
};

export const getDataType = () => [
  {
    id: DataType.Heating,
    title: t`Heating (MWh)`,
  },
  {
    id: DataType.HeatingNormalized,
    title: t`Heating (MWh), normalized`,
  },
  {
    id: DataType.Electricity,
    title: t`Electricity (kWh)`,
  },
  {
    id: DataType.Cooling,
    title: t`Cooling (MWh)`,
  },
  {
    id: DataType.HotWater,
    title: t`Hot water (m3)`,
  },
  {
    id: DataType.ColdWater,
    title: t`Cold water (m3)`,
  },
  {
    id: DataType.TotalWater,
    title: t`Total water (m3)`,
  },
];

export type DateRange = { start: string; end: string };

export enum DateResolution {
  year = "year",
  month = "month",
  day = "day",
}

export const formatRange = (range: DateRange): DateRange => {
  const startDate = DateTime.fromISO(range.start, { zone: "utc" }).startOf(
    "day",
  );
  const endDate = DateTime.fromISO(range.end, { zone: "utc" }).endOf("day");

  return {
    start: startDate.toISO({ includeOffset: false }),
    end: endDate.toISO({ includeOffset: false }),
  };
};

export function getAxisLabelTranslations(resolution: DateResolution): string {
  const axisLabels: Record<DateResolution, string> = {
    year: t`Months`,
    month: t`Days`,
    day: t`Hours`,
  };

  return axisLabels[resolution];
}

export function getResolutionLabel(resolution: DateResolution): string {
  const resolutionLabel: Record<DateResolution, string> = {
    year: t`Year`,
    month: t`Month`,
    day: t`Day`,
  };

  return resolutionLabel[resolution];
}

export enum CellFormat {
  DECIMAL = "decimal",
  PERCENT = "percent",
}

const getFormatter = (locale: string, style: CellFormat) =>
  new Intl.NumberFormat(locale, {
    style,
    minimumFractionDigits: 3,
    maximumFractionDigits: 3,
  });

export const formatValue = (
  v: number | null,
  format: CellFormat = CellFormat.DECIMAL,
): string => {
  if (v == null) return "-";
  const locale = i18n.locale || defaultLocale;
  const formatter = getFormatter(locale, format);
  return formatter.format(v);
};

export const getPercentChange = (y1, y2): number | null => {
  if (y1 == null || y2 == null || y2 === 0) {
    return null;
  }
  const result = (y1 - y2) / y2;
  return isFinite(result) ? result : null;
};

export const getValueChange = (y1, y2) => {
  if (y1 == null || y2 == null) return null;
  return y1 - y2;
};

export const getHeaders = (year: number, comparisonYears: number[]) => {
  const arr = [t`Consumption ${year}`];

  if (comparisonYears.length) {
    comparisonYears.forEach((diff) => {
      arr.push(
        t`Change from ${year - diff} (%)`,
        t`Change from ${year - diff}`,
        t`Consumption ${year - diff}`,
      );
    });
  }
  return arr;
};
export type NormalizationResult =
  | { success: true; normalizedValues: (number | null)[] }
  | { success: false; error: string };

export function normalizeHeatingEnergy(
  measuredValues: number[],
  location: Location | null | undefined,
  fromDate: string,
  toDate: string,
  resolution: DateResolution,
): NormalizationResult {
  // Edge case: missing reference location
  if (!location) {
    return {
      success: false,
      error: t`The selected building is missing a reference location.`,
    };
  }

  // Edge case: missing SN (normal-year HDD)
  const SN = location.normalFactor;
  if (SN === null || SN === undefined) {
    return {
      success: false,
      error: t`No normal-year heating degree days are available for the selected building.`,
    };
  }

  const from = DateTime.fromISO(fromDate, { zone: "utc" });
  const to = DateTime.fromISO(toDate, { zone: "utc" }).endOf("day");

  const monthYearPairs: { month: number; year: number }[] = [];

  const unit = resolution === DateResolution.year ? "month" : "day";

  const stepByResolution: Record<DateResolution, { [key: string]: number }> = {
    [DateResolution.year]: { month: 1 },
    [DateResolution.month]: { day: 1 },
    [DateResolution.day]: { hour: 1 },
  };

  let current = from.startOf(unit);

  while (current <= to) {
    monthYearPairs.push({ month: current.month, year: current.year });
    current = current.plus(stepByResolution[resolution]);
  }

  const monthYearToNormalFactorMap = new Map<string, number>();

  for (const { month, year } of monthYearPairs) {
    const factor = (location.monthlyNormalizationFactors || []).find(
      (f) => f.year === year && f.month === month,
    );

    const key = `${year}-${month}`;

    if (factor && factor.normalizationValue !== null) {
      monthYearToNormalFactorMap.set(key, factor.normalizationValue);
    }
  }

  // Apply normalization
  const normalizedValues = measuredValues.map((qTotal, index) => {
    const monthYearPair = monthYearPairs[index];
    if (!monthYearPair) {
      return null;
    }
    const { month, year } = monthYearPair;
    const key = `${year}-${month}`;

    const S_actual = monthYearToNormalFactorMap.get(key);

    // If normalization factor is missing for this month, return null
    if (S_actual === undefined || S_actual === null) {
      return null;
    }

    // Edge case: S_actual = 0 -> unnormalized
    if (S_actual === 0) {
      return qTotal;
    }

    // calculate normalization
    return (SN / S_actual) * (qTotal * 0.75) + qTotal * 0.25;
  });

  return {
    success: true,
    normalizedValues,
  };
}

export function getTimePeriods(
  resolution: DateResolution,
  startDate: string,
): string[] {
  let names: string[] = [];
  if (resolution === DateResolution.year) {
    names = [
      t`Jan`,
      t`Feb`,
      t`Mar`,
      t`Apr`,
      t`May_short`,
      t`Jun`,
      t`Jul`,
      t`Aug`,
      t`Sep`,
      t`Oct`,
      t`Nov`,
      t`Dec`,
    ];
  } else if (resolution === DateResolution.month) {
    if (startDate) {
      const date = DateTime.fromISO(startDate);
      const daysInMonth = date.daysInMonth;
      names = Array.from({ length: daysInMonth }, (_, i) => String(i + 1));
    }
  } else if (resolution === DateResolution.day) {
    names = Array.from(
      { length: 24 },
      (_, i) => String(i).padStart(2, "0") + ":00",
    );
  }

  return names;
}

type GetReportData = {
  energyReport?: PointEnergyReportDTO[];
  dataType?: DataType;
  start: string;
  end: string;
  location?: Location | null;
  resolution: DateResolution;
};

type ConvertToEnergyReport = {
  values: number[];
  dataType: DataType;
} & Pick<GetReportData, "start" | "end" | "resolution" | "location">;

function validateRawValues({
  values,
  dataType,
  resolution,
  location,
  start,
  end,
}: ConvertToEnergyReport): { values: (number | null)[]; error?: string } {
  let actualValues: (number | null)[] = values;

  if (dataType === DataType.HeatingNormalized) {
    const result = normalizeHeatingEnergy(
      values,
      location,
      start,
      end,
      resolution,
    );

    if (result.success) {
      actualValues = result.normalizedValues;
    } else {
      return { values: actualValues, error: result.error };
    }
  }
  return { values: actualValues };
}

export function getReportData({
  energyReport,
  dataType,
  start,
  end,
  location,
  resolution,
}: GetReportData) {
  if (!energyReport || !dataType || !energyReport.length) {
    return [];
  }

  return Object.values(energyReport).map((val) => {
    const { values, error } = validateRawValues({
      values: val.values,
      dataType,
      end,
      resolution,
      start,
      location,
    });
    return {
      name: val.name,
      deviceId: val.deviceId,
      description: val.description,
      values,
      error,
    };
  });
}

export function getComparisonYearOptions(year: number) {
  return [
    { label: String(year - 2), value: "2" },
    { label: String(year - 1), value: "1" },
  ];
}

export function getFavoriteComparisonYearOptions() {
  return [
    { label: t`2 years ago`, value: "2" },
    { label: t`1 year ago`, value: "1" },
  ];
}

export function getComparisonQueryRanges(
  comparisonYears: number[],
  start?: string,
  end?: string,
) {
  return comparisonYears
    .map((yearDiff) => {
      if (!start || !end) {
        return null;
      }

      const startDate = DateTime.fromISO(start);
      const endDate = DateTime.fromISO(end);

      const comparisonStart = startDate.minus({ years: yearDiff });
      const comparisonEnd = endDate.minus({ years: yearDiff });

      return {
        start: comparisonStart.toISO({ includeOffset: false }),
        end: comparisonEnd.toISO({ includeOffset: false }),
      };
    })
    .filter((range): range is { start: string; end: string } => range !== null);
}
